import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AudioPlayer from "./AudioPlayer";
import { useTranslation } from "react-i18next";
import { Music, Video, Image as ImageIcon, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type TalentMedia = {
  id: string;
  media_type: "audio" | "video" | "image";
  url: string;
  title: string | null;
  display_order: number;
};

interface TalentMediaGalleryProps {
  talentId: string;
  talentName: string;
}

export const TalentMediaGallery = ({ talentId, talentName }: TalentMediaGalleryProps) => {
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState<TalentMedia | null>(null);

  const { data: mediaItems } = useQuery({
    queryKey: ["public-talent-media", talentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent_media")
        .select("*")
        .eq("talent_id", talentId)
        .order("display_order");
      if (error) throw error;
      return data as TalentMedia[];
    },
  });

  if (!mediaItems || mediaItems.length === 0) return null;

  const audioItems = mediaItems.filter((m) => m.media_type === "audio");
  const videoItems = mediaItems.filter((m) => m.media_type === "video");
  const imageItems = mediaItems.filter((m) => m.media_type === "image");

  return (
    <div className="space-y-8">
      {/* Audio Section */}
      {audioItems.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wide font-medium mb-4 flex items-center gap-2 text-primary">
            <Music size={16} />
            {t("talentProfile.voiceDemo")}
          </h2>
          <div className="space-y-3">
            {audioItems.map((item) => (
              <div key={item.id}>
                <AudioPlayer
                  src={item.url}
                  title={item.title || `${talentName} - Demo`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Section */}
      {videoItems.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wide font-medium mb-4 flex items-center gap-2 text-primary">
            <Video size={16} />
            {t("talentProfile.videoDemo", "Video Demos")}
          </h2>
          <div className="space-y-2">
            {videoItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveVideo(item)}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors group"
              >
                <Play size={16} className="group-hover:scale-110 transition-transform" />
                <span className="underline underline-offset-2">
                  {item.title || `${talentName} - Video Demo`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Video Modal */}
      <Dialog open={!!activeVideo} onOpenChange={(open) => !open && setActiveVideo(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="p-4 pb-0 shrink-0">
            <DialogTitle className="text-base">
              {activeVideo?.title || `${talentName} - Video Demo`}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 pt-2 min-h-0 flex-1 flex items-center">
            {activeVideo && (
              <video
                src={activeVideo.url}
                controls
                autoPlay
                className="w-full max-h-[calc(90vh-5rem)] rounded-lg bg-muted object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Section */}
      {imageItems.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wide font-medium mb-4 flex items-center gap-2 text-primary">
            <ImageIcon size={16} />
            {t("talentProfile.gallery", "Gallery")}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {imageItems.map((item) => (
              <div key={item.id} className="aspect-square bg-muted overflow-hidden rounded-lg">
                <img
                  src={item.url}
                  alt={item.title || talentName}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
